import { LitElement, html, css, nothing, unsafeCSS, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { styleMap } from "lit/directives/style-map.js";
import { classMap } from "lit/directives/class-map.js";
import {
  isDefaultColorToken,
  resolveAccentColorForToken,
  resolveColor,
  resolveInkColorForToken,
} from "../helper/utils/color-resolver";
import { cssOnFillEnhance, ON_COLOR_FALLBACK_LIGHT } from "../helper/utils/contrast-color";
import { resolveRadius } from "../helper/utils/size-resolver";
import { PresetController } from "../helper/controllers/preset-controller";
import { SkyIcon } from "../sky-icon/sky-icon.js";
import { skyFocusRingHostStyles } from "../helper/styles/focus-ring";

export type SkyChipVariant =
  | "solid"
  | "flat"
  | "shadow"
  | "bordered"
  | "faded"
  | "light"
  | "dot";
export type SkyChipRemovedEventDetail = { label: string };

/**
 * @element sky-chip
 *
 * @summary Customizable chip for tags or selections with icon support, removable action, loading state, and visual variants.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/chip
 * @dependency sky-icon
 *
 * @slot - Default slot for the chip label or any other content.
 * @slot prefix - Optional prefix content that can be placed before the label.
 * @slot suffix - Optional suffix content that can be placed after the label.
 *
 * @csspart chip - The visual representation of the chip component.
 * @csspart icon - The icons displayed within the chip.
 * @csspart close-button - The button for removing the chip.
 * @csspart loading-spinner - The spinner that appears during loading.
 * @csspart error-message - The error message displayed for invalid chips.
 *
 * @property {string} label - The label displayed on the chip. Default: `""`.
 * @property {boolean} removable - Whether the chip can be removed by the user. Default: `false`.
 * @property {string} color - The background color of the chip, can accept tokens or custom CSS color. Default: `"primary"`.
 * @property {string} radius - The border radius of the chip. Default: `"full"`.
 * @property {string} size - The size of the chip, which affects padding and font size. Default: `"md"`.
 * @property {string} iconL - Left icon; shown only when the `prefix` slot is empty. Default: `null`.
 * @property {string} iconR - Right icon; shown only when the `suffix` slot is empty. Default: `null`.
 * @property {boolean} loading - Whether the chip is in a loading state. Default: `false`.
 * @property {boolean} disabled - Whether the chip is disabled. Default: `false`.
 * @property {SkyChipVariant} variant - Visual style variant. Default: `"flat"`.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method removeChip Dispatches `chip-removed` and removes the chip from the DOM.
 *
 * @fires {CustomEvent<SkyChipRemovedEventDetail>} chip-removed - Fired when the chip is removed by user action.
 *
 * @Behavior
 * - Uses default slot content when present, otherwise falls back to `label`.
 * - `iconL` / `iconR` render only when `prefix` / `suffix` slots have no meaningful content.
 * - Emits `chip-removed` and removes itself when removable close action is triggered.
 * - Resolves color tokens and radius/size presets to CSS values at render time.
 *
 * @example
 * ```html
 * <sky-chip label="Active Filter" removable iconl="ion:funnel" variant="light"></sky-chip>
 * ```
 * ```vue
 * <template>
 *   <sky-chip label="Active Filter" removable iconl="ion:funnel" variant="light"></sky-chip>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return <sky-chip label="Active Filter" removable iconl="ion:funnel" variant="light"></sky-chip>;
 * }
 * ```
 */
@customElement("sky-chip")
export class SkyChip extends LitElement {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  @property({ type: String }) label: string = "";
  @property({ type: Boolean }) removable: boolean = false;

  /** Accepts preset tokens OR any valid CSS color (e.g., #0af, rgb(), var(--x)) */
  @property({ type: String }) color: string = "primary";

  /** Radius can be any CSS radius value */
  @property({ type: String }) radius: string = "full";

  /** Size controls padding + font-size (xs, sm, md, lg, xl or numeric like "14px") */
  @property({ type: String }) size: string = "md";

  @property({ type: String }) iconL: string | null = null;
  @property({ type: String }) iconR: string | null = null;
  @property({ type: Boolean }) loading: boolean = false;
  @property({ type: Boolean, reflect: true }) disabled: boolean = false;
  @property({ type: String, reflect: true }) variant: SkyChipVariant = "flat";

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = [
    skyFocusRingHostStyles,
    css`
    :host {
      display: inline-block;
      font-family: var(--sky-font);
      z-index: 1;
      --sky-focus-ring-color: var(--chip-bg-color, var(--sky-active-primary));
    }

    :host([disabled]) {
      pointer-events: none;
      opacity: var(--sky-opacity-disabled, 0.6);
    }

    .chip {
      position: relative;
      display: flex;
      gap: 2px;
      width: max-content;
      align-items: center;
      border-radius: var(--sky-radius-secondary);
      color: var(--chip-text-color);
      font-weight: var(--sky-font-weight-medium, 500);
      transition: background-color 0.3s ease, color 0.3s ease,
        box-shadow 0.2s ease, transform 0.2s ease;
      cursor: pointer;
      background-color: var(--chip-bg-color);
    }

    .close-button {
      position: relative;
      display: flex;
      height: 18px;
      width: 18px;
      justify-content: center;
      font-size: inherit;
      align-items: center;
      background-color: var(--sky-hover-primary);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border: none;
      border-radius: var(--sky-radius-full);
      cursor: pointer;
      color: var(--sky-text-primary);
      transition: color 0.2s ease, background-color 0.2s ease;
    }

    .close-button sky-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      transition: color 0.3s ease;
    }

    :dir(rtl) .close-button {
      margin-right: var(--sky-space-1, 5px);
      margin-left: unset;
    }

    .close-button:hover {
      color: var(--sky-danger-primary);
    }

    .close-button:focus-visible {
      outline: none;
      box-shadow: var(--sky-focus-ring);
    }

    .close-button:hover sky-icon,
    .close-button:focus-visible sky-icon{
      color: var(--sky-danger-primary);
      outline: none;
    }

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--chip-text-color);
    }

    sky-icon {
      display: inline-block;
      width: 1em;
      height: 1em;
    }

    ::slotted([slot="prefix"]),
    ::slotted([slot="suffix"]) {
      display: inline-flex;
      align-items: center;
    }

    .loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--sky-overlay-primary);
      border-radius: inherit;
    }

    .loading-spinner {
      width: 14px;
      height: 14px;
      border: 2px solid var(--sky-border-light, var(--sky-text-tertiary));
      border-top-color: var(--chip-bg-color);
      border-radius: var(--sky-radius-full);
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .chip,
      .close-button,
      .close-button sky-icon {
        transition: none;
      }
      .loading-spinner {
        animation: none;
      }
    }

    .chip.bordered {
      border: 1px solid var(--chip-ink-color, var(--chip-bg-color));
      color: var(--chip-ink-color, var(--chip-bg-color));
      background: transparent;
    }

    .chip.light {
      background: transparent;
      color: var(--chip-ink-color, var(--chip-bg-color));
    }

    .chip.flat {
      box-shadow: none;
      background: color-mix(
        in srgb,
        var(--chip-fill-color, var(--chip-bg-color)) 20%,
        transparent
      );
      color: var(--chip-ink-color, var(--chip-bg-color));
    }

    .chip.faded {
      background-color: var(--sky-glass-primary);
      border: 1px solid var(--sky-text-tertiary);
      color: var(--chip-ink-color, var(--chip-bg-color));
    }

    .chip.shadow {
      box-shadow: var(--sky-box-shadow-primary);
    }

    .chip.shadow:hover {
      box-shadow: var(--sky-box-shadow-hover);
    }

    .chip.faded .icon,
    .chip.flat .icon,
    .chip.light .icon,
    .chip.dot .icon,
    .chip.bordered .icon {
      color: var(--chip-ink-color, var(--chip-bg-color));
    }

    .chip.dot {
      padding-left: var(--sky-space-5, 20px) !important;
      position: relative;
      background-color: transparent;
      border: 1px solid var(--sky-text-tertiary);
      color: var(--sky-text-primary);
    }

    .chip.dot::before {
      content: "";
      position: absolute;
      left: var(--sky-space-1\\.5, 6px);
      top: 50%;
      transform: translateY(-50%);
      width: var(--sky-space-2, 8px);
      height: var(--sky-space-2, 8px);
      border-radius: var(--sky-radius-full);
      background-color: var(--chip-accent-color, var(--chip-bg-color));
    }

    /* Solid / shadow chips: enhance label + icon FG against fill. */
    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector: ".chip.solid, .chip.shadow",
          fillVar: "--chip-bg-color",
        },
        {
          selector: ".chip.solid .icon, .chip.shadow .icon",
          fillVar: "--chip-bg-color",
        },
      ]),
    )}
  `,
  ];

  private _prefixFilled = false;
  private _suffixFilled = false;
  private _defaultFilled = false;
  private _slotFlagsSeeded = false;

  /** Updates host CSS variables from current color token/value. */
  protected override updated(changed: PropertyValues): void {
    if (!changed.has("color")) return;

    const bgColor = resolveColor(this.color);
    const textColor = isDefaultColorToken(this.color)
      ? "var(--sky-text-primary)"
      : ON_COLOR_FALLBACK_LIGHT;

    this.style.setProperty("--chip-bg-color", bgColor);
    this.style.setProperty("--chip-text-color", textColor);

    if (isDefaultColorToken(this.color)) {
      this.style.setProperty("--chip-ink-color", resolveInkColorForToken(this.color));
      this.style.setProperty(
        "--chip-accent-color",
        resolveAccentColorForToken(this.color),
      );
      this.style.setProperty("--chip-fill-color", "var(--sky-hover-tertiary)");
    } else {
      this.style.removeProperty("--chip-ink-color");
      this.style.removeProperty("--chip-accent-color");
      this.style.removeProperty("--chip-fill-color");
    }
  }

  /** Size resolver similar to button: controls padding + font-size */
  private _resolveSize(
    input: string
  ): {
    padding: string;
    fontSize: string;
  } {
    const presets: Record<string, { padding: string; fontSize: string }> = {
      xs: { padding: "2px 4px", fontSize: "11px" },
      sm: { padding: "3px 6px", fontSize: "12px" },
      md: { padding: "4px 8px", fontSize: "13px" },
      lg: { padding: "5px 10px", fontSize: "14px" },
      xl: { padding: "6px 12px", fontSize: "15px" },
    };

    if (presets[input]) return presets[input];

    const m = String(input).trim().match(/^([\d.]+)(px|rem|em)?$/i);
    const unit = m?.[2] || "px";
    const fsNum = m ? parseFloat(m[1]) : 13;
    const fs = `${fsNum}${unit}`;

    const clamp = (n: number, min: number, max: number) =>
      Math.max(min, Math.min(max, n));

    const py = clamp(fsNum * 0.4, 2, 10);
    const px = clamp(fsNum * 0.8, 6, 16);

    const to = (n: number) => `${Math.round(n * 100) / 100}${unit}`;

    return {
      padding: `${to(py)} ${to(px)}`,
      fontSize: fs,
    };
  }

  /**
   * Dispatches `chip-removed` and removes the chip from the DOM.
   *
   * @returns {void}
   */
  public removeChip(): void {
    const removeEvent = new CustomEvent<SkyChipRemovedEventDetail>("chip-removed", {
      detail: { label: this.label },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(removeEvent);
    this.remove();
  }

  private _slotHasMeaningfulContent(slotEl: HTMLSlotElement): boolean {
    const nodes = slotEl.assignedNodes({ flatten: true });
    return nodes.some((n) =>
      n.nodeType === Node.ELEMENT_NODE
        ? true
        : (n.textContent ?? "").trim().length > 0
    );
  }

  /** Light DOM check works before the shadow tree exists (first render). */
  private _hasPrefixSlotContent(): boolean {
    return this._prefixFilled;
  }

  private _hasSuffixSlotContent(): boolean {
    return this._suffixFilled;
  }

  private _hasDefaultSlotContent(): boolean {
    return this._defaultFilled;
  }

  private _seedSlotFlagsFromLightDom(): void {
    this._prefixFilled = !!this.querySelector('[slot="prefix"]');
    this._suffixFilled = !!this.querySelector('[slot="suffix"]');
    this._defaultFilled = false;
    for (const n of Array.from(this.childNodes)) {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const slot = (n as Element).getAttribute("slot");
        if (slot === "prefix" || slot === "suffix") continue;
        this._defaultFilled = true;
        break;
      }
      if ((n.textContent ?? "").trim().length > 0) {
        this._defaultFilled = true;
        break;
      }
    }
    this._slotFlagsSeeded = true;
  }

  override willUpdate(): void {
    if (!this._slotFlagsSeeded) this._seedSlotFlagsFromLightDom();
  }

  private _onSlotChange = (e: Event): void => {
    const slot = e.target as HTMLSlotElement | null;
    if (!slot || slot.tagName !== "SLOT") return;
    this._slotFlagsSeeded = true;
    const filled = this._slotHasMeaningfulContent(slot);
    const name = slot.name;
    if (name === "prefix") {
      if (this._prefixFilled === filled) return;
      this._prefixFilled = filled;
    } else if (name === "suffix") {
      if (this._suffixFilled === filled) return;
      this._suffixFilled = filled;
    } else {
      if (this._defaultFilled === filled) return;
      this._defaultFilled = filled;
    }
    this.requestUpdate();
  };

  override render() {
    const sizeDef = this._resolveSize(this.size);
    const chipStyle = {
      padding: sizeDef.padding,
      borderRadius: resolveRadius(this.radius),
      fontSize: sizeDef.fontSize,
    };

    return html`
      <div
        class=${classMap({
          chip: true,
          [this.variant]: true,
          loading: this.loading,
        })}
        style=${styleMap(chipStyle)}
        part="chip"
      >
        <slot name="prefix" @slotchange=${this._onSlotChange}></slot>
        ${when(!this._hasPrefixSlotContent() && this.iconL, () => html`
          <sky-icon class="icon" part="icon" icon="${this.iconL!}"></sky-icon>
        `)}
        <slot @slotchange=${this._onSlotChange}></slot>
        ${this._hasDefaultSlotContent() ? nothing : this.label}
        <slot name="suffix" @slotchange=${this._onSlotChange}></slot>
        ${when(!this._hasSuffixSlotContent() && this.iconR, () => html`
          <sky-icon class="icon icon-right" part="icon" icon="${this.iconR!}"></sky-icon>
        `)}
        ${when(this.removable, () => html`
          <button
            class="close-button"
            part="close-button"
            type="button"
            aria-label="Remove chip"
            @click=${this.removeChip}
          >
            <sky-icon icon="ion:close"></sky-icon>
          </button>
        `)}
        ${when(this.loading, () => html`
          <div class="loading-overlay" part="loading-overlay">
            <div class="loading-spinner" part="loading-spinner"></div>
          </div>
        `)}
      </div>
    `;
  }
}